/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/ui/utils/tailwind';

/** Distance between the trigger and the bubble, in pixels. */
const GAP = 8;

/** Breathing room kept between the bubble and the viewport edge. */
const EDGE_MARGIN = 8;

/** Where the bubble sits relative to its own anchor point. */
const TRANSFORMS = {
  top: 'translate(-50%, -100%)',
  bottom: 'translate(-50%, 0)',
  left: 'translate(-100%, -50%)',
  right: 'translate(0, -50%)',
} as const;

/** The viewport point the bubble hangs from, per side. */
function anchorFor(rect: DOMRect, position: keyof typeof TRANSFORMS) {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  if (position === 'top') return { left: centerX, top: rect.top - GAP };
  if (position === 'bottom') return { left: centerX, top: rect.bottom + GAP };
  if (position === 'left') return { left: rect.left - GAP, top: centerY };
  return { left: rect.right + GAP, top: centerY };
}

export type TooltipProps = {
  label: string;
  children: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
};

/**
 * Tooltip component with Tailwind styling
 * Replaces Mantine Tooltip
 */
export const Tooltip: React.FC<TooltipProps> = ({
  label,
  children,
  position = 'top',
}) => {
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  /**
   * How far the bubble had to slide to stay on screen.
   *
   * A bubble is centred on its trigger, so one hanging off a control near
   * the right edge ran past it and had its text cut off. The slide keeps the
   * box in view; the arrow is pushed the other way by the same amount so it
   * still points at the control.
   */
  const [shift, setShift] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const triggerRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const isVisible = coords !== null;

  const horizontal = position === 'top' || position === 'bottom';

  useLayoutEffect(() => {
    if (!isVisible || !horizontal) {
      setShift(0);
      return;
    }
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!bubble) return;
    const overRight = bubble.right - (window.innerWidth - EDGE_MARGIN);
    const overLeft = EDGE_MARGIN - bubble.left;
    const next = overRight > 0 ? -overRight : overLeft > 0 ? overLeft : 0;
    // Compared against the current shift because this runs on the already
    // shifted box; without it the correction would compound every pass.
    if (Math.abs(next) > 0.5) setShift((current) => current + next);
  }, [isVisible, horizontal, coords]);

  // Measured on show rather than tracked: the bubble only lives while
  // the pointer rests, and a hovered control does not move underneath it.
  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setShift(0);
      setCoords(anchorFor(rect, position));
    }
  }, [position]);

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setCoords(null), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const arrowClasses = {
    top: 'before:top-full before:left-[var(--tt-arrow-x,50%)] before:-translate-x-1/2 before:border-t-[var(--surface-inverse)]',
    bottom: 'before:bottom-full before:left-[var(--tt-arrow-x,50%)] before:-translate-x-1/2 before:border-b-[var(--surface-inverse)]',
    left: 'before:left-full before:top-1/2 before:-translate-y-1/2 before:border-l-[var(--surface-inverse)]',
    right: 'before:right-full before:top-1/2 before:-translate-y-1/2 before:border-r-[var(--surface-inverse)]',
  };

  return (
    <div
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {/* Portaled to the body with fixed coordinates. Rendered in place
          it was an `absolute` child of whatever card held the trigger,
          so any ancestor that clipped or stacked -- the app's left bar,
          a panel with its own overflow -- cut the bubble in half. */}
      {isVisible && createPortal(
        <div
          ref={bubbleRef}
          className={cn(
            'fixed z-[1000] px-2 py-1 text-xs text-[var(--text-inverse)] bg-[var(--surface-inverse)] rounded',
            'w-max whitespace-normal pointer-events-none',
            // Never wider than the viewport allows, so a long label wraps
            // instead of running off the side on a narrow screen.
            'max-w-[min(20rem,calc(100vw-16px))]',
            'before:content-[""] before:absolute before:border-4 before:border-transparent',
            arrowClasses[position],
          )}
          style={{
            left: coords.left + shift,
            top: coords.top,
            transform: TRANSFORMS[position],
            // The arrow tracks the trigger rather than the shifted box.
            '--tt-arrow-x': `calc(50% - ${shift}px)`,
          } as React.CSSProperties}
          role="tooltip"
        >
          {label}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default Tooltip;
